/**
 * Entra ID sign-in.
 *
 * The City's established SPA pattern, as used by watchnet.swiftcurrent.ca on
 * the same app registration: MSAL, full-page redirect, and the Entra **ID
 * token** sent as the Bearer. The backend validates signature, issuer,
 * audience (the client ID) and expiry -- see backend/odie/auth/entra.py.
 *
 * Where Odie deliberately differs from Watchnet:
 *
 *  - **localStorage, not sessionStorage.** Every double-clicked PDF opens a
 *    new app window (launch_type "multiple-clients"). sessionStorage is
 *    per-window, so each one would start signed out and bounce through the
 *    login page. localStorage is shared, so a new window is signed in already.
 *
 *  - **Redirect, never popup.** The vhost sends
 *    Cross-Origin-Opener-Policy: same-origin, which severs a popup from its
 *    opener the moment it reaches login.microsoftonline.com; MSAL then sees
 *    the popup as closed. Loosening that header to make popups work is the
 *    wrong trade, so launched files and the open document are carried across
 *    the redirect instead (auth/carry.ts).
 *
 *  - **No hidden-iframe renewal.** The CSP has frame-ancestors 'none', so the
 *    redirect page cannot load in MSAL's iframe; it would sit out a 10-second
 *    timeout before failing. Silent renewal is limited to the cached refresh
 *    token, and when that has expired sign-in is asked for explicitly.
 */

import {
  BrowserAuthError,
  CacheLookupPolicy,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser'

import { setTokenProvider } from '../api'
import { allowLeaving } from '../state/leave'

// User.Read yields an ID token, and matches the scope the City's other apps
// already have consent for on this registration -- no new consent prompt.
const SCOPES = ['User.Read']

// Refresh an ID token this close to expiry rather than send one that dies in
// flight; a 150 MB upload can easily outlast a nearly-expired token.
const EXPIRY_MARGIN_MS = 5 * 60 * 1000

// Two redirects this close together mean sign-in is not completing -- an
// AADSTS error page the user backed out of, or a misconfigured redirect URI.
// Stop and say so instead of looping.
const LOOP_WINDOW_MS = 30 * 1000
const LOOP_KEY = 'odie.auth.lastRedirect'

interface AuthConfig {
  authRequired: boolean
  tenantId: string
  clientId: string
}

export type AuthOutcome =
  | { kind: 'disabled' }
  | { kind: 'signed-in'; account: AccountInfo }
  | { kind: 'redirecting' }

let pca: PublicClientApplication | null = null
let account: AccountInfo | null = null
let started: Promise<AuthOutcome> | null = null
let beforeRedirect: (() => Promise<void>) | null = null
let onExpired: (() => void) | null = null

/** Runs just before leaving for the login page; used to stash open work. */
export function setBeforeRedirect(hook: () => Promise<void>): void {
  beforeRedirect = hook
}

/** Called when a signed-in session can no longer get a token silently. */
export function onSignInExpired(listener: () => void): void {
  onExpired = listener
}

export function currentAccount(): AccountInfo | null {
  return account
}

/** Start sign-in. Idempotent: StrictMode and retries share one attempt. */
export function initAuth(): Promise<AuthOutcome> {
  started ??= start().catch((err) => {
    started = null
    throw err
  })
  return started
}

async function start(): Promise<AuthOutcome> {
  const response = await fetch('/api/auth-config')
  if (!response.ok) {
    throw new Error(`Could not load sign-in settings from the server (${response.status}).`)
  }
  const cfg = (await response.json()) as AuthConfig
  if (!cfg.authRequired) return { kind: 'disabled' }
  if (!cfg.tenantId || !cfg.clientId) {
    throw new Error(
      'Sign-in is required, but the server has no tenant or client ID. ' +
        'Set ODIE_TENANT_ID and ODIE_CLIENT_ID in /etc/odie/odie.env.',
    )
  }

  pca = new PublicClientApplication({
    auth: {
      clientId: cfg.clientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
      // Registered as an SPA redirect URI on the shared app registration.
      redirectUri: `${window.location.origin}/`,
      // Land on / after sign-in rather than bouncing back to /open: the launch
      // queue there would be empty anyway, and carried files are restored
      // wherever the app starts.
      navigateToLoginRequestUrl: false,
    },
    cache: { cacheLocation: 'localStorage' },
  })
  await pca.initialize()

  let result: AuthenticationResult | null = null
  try {
    result = await pca.handleRedirectPromise()
  } catch (err) {
    if (!isInteractionLock(err)) throw describe(err)
    // A sign-in abandoned mid-flight leaves MSAL's lock set, and every later
    // attempt fails with interaction_in_progress until it is cleared.
    clearInteractionLock()
  }

  account =
    result?.account ?? pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null
  if (account) pca.setActiveAccount(account)
  setTokenProvider(token)

  if (!account) {
    await redirectToSignIn()
    return { kind: 'redirecting' }
  }

  // Prove the cached account still works now, on the splash screen, rather
  // than on the user's first click with a document half-open.
  try {
    await idToken()
  } catch (err) {
    if (!needsInteraction(err)) throw describe(err)
    await redirectToSignIn()
    return { kind: 'redirecting' }
  }

  sessionStorage.removeItem(LOOP_KEY)
  return { kind: 'signed-in', account }
}

/** Leave for Microsoft's sign-in page, after stashing anything open. */
export async function redirectToSignIn(): Promise<void> {
  if (!pca) throw new Error('Sign-in has not started.')

  const last = Number(sessionStorage.getItem(LOOP_KEY) ?? 0)
  if (Date.now() - last < LOOP_WINDOW_MS) {
    sessionStorage.removeItem(LOOP_KEY)
    throw new Error(
      'Sign-in did not complete. If Microsoft showed an error, it will name ' +
        'the problem -- AADSTS50011 means this address is not yet a redirect ' +
        'URI on the app registration.',
    )
  }
  sessionStorage.setItem(LOOP_KEY, String(Date.now()))

  await beforeRedirect?.()
  allowLeaving()
  await pca.loginRedirect({
    scopes: SCOPES,
    // With a known account this is re-authentication: Entra skips the account
    // picker, and on a City device usually the password too.
    account: account ?? undefined,
    loginHint: account?.username,
  })
}

/** The token provider handed to the API client. */
async function token(): Promise<string | null> {
  if (!pca || !account) return null
  try {
    return await idToken()
  } catch (err) {
    if (!needsInteraction(err)) throw describe(err)
    // The request that needed this token is left pending: the editor shows a
    // sign-in prompt over the document, and continuing from it navigates away.
    // Rejecting instead would flash an error for something that is not one.
    onExpired?.()
    return new Promise<string>(() => {})
  }
}

async function idToken(): Promise<string> {
  const request = {
    scopes: SCOPES,
    account: account!,
    cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
  }
  let result = await pca!.acquireTokenSilent(request)
  if (expiresSoon(result)) {
    // MSAL judges freshness by the access token, so it can hand back a cached
    // ID token that is already expired. Redeem the refresh token for a new one.
    result = await pca!.acquireTokenSilent({
      ...request,
      cacheLookupPolicy: CacheLookupPolicy.RefreshToken,
    })
  }
  return result.idToken
}

function expiresSoon(result: AuthenticationResult): boolean {
  const exp = (result.idTokenClaims as { exp?: number } | undefined)?.exp
  return !result.idToken || !exp || exp * 1000 - Date.now() < EXPIRY_MARGIN_MS
}

function needsInteraction(err: unknown): boolean {
  return (
    err instanceof InteractionRequiredAuthError ||
    (err instanceof BrowserAuthError && err.errorCode === 'no_account_error')
  )
}

function isInteractionLock(err: unknown): boolean {
  return err instanceof BrowserAuthError && err.errorCode === 'interaction_in_progress'
}

function clearInteractionLock(): void {
  for (const key of Object.keys(sessionStorage)) {
    if (key.startsWith('msal.') && key.endsWith('interaction.status')) {
      sessionStorage.removeItem(key)
    }
  }
}

/** MSAL errors carry the useful part in errorMessage, not message. */
function describe(err: unknown): Error {
  if (err instanceof BrowserAuthError || err instanceof InteractionRequiredAuthError) {
    return new Error(err.errorMessage || err.errorCode || String(err))
  }
  return err instanceof Error ? err : new Error(String(err))
}

/**
 * Delegated Microsoft Graph permissions for OneDrive and SharePoint. They
 * need admin consent on the app registration (docs/DEPLOYMENT.md); the
 * browser then calls Graph directly as the user, so Odie's server never
 * holds a Graph token and can reach nothing the user cannot.
 */
export const GRAPH_SCOPES = ['Files.ReadWrite.All', 'Sites.Read.All']

/** Whether sign-in is on, and so whether OneDrive can be offered at all. */
export function graphAvailable(): boolean {
  return Boolean(pca && account)
}

/**
 * An access token for Graph. The first time (before the permissions are
 * granted, or when the refresh token has lapsed) this leaves for Microsoft
 * and does not return; the open document is carried across as for sign-in.
 */
export async function graphToken(): Promise<string> {
  if (!pca || !account) throw new Error('OneDrive needs sign-in, which is off on this server.')
  try {
    const result = await pca.acquireTokenSilent({ scopes: GRAPH_SCOPES, account })
    return result.accessToken
  } catch (err) {
    if (!needsInteraction(err)) throw describe(err)
    await beforeRedirect?.()
    allowLeaving()
    await pca.acquireTokenRedirect({
      scopes: GRAPH_SCOPES, account, loginHint: account.username,
    })
    return new Promise<string>(() => {})
  }
}
